import { InventorySKUListing } from '@views/StockBySKU/components/types';
import { ChevronRightIcon, CheckIcon } from '@heroicons/react/20/solid';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { Badge } from '@tailwindComponents/Badge';
import { EntityTile } from '@tailwindComponents/EntityTile';
import { numberToLocale } from '@forms/client/AddProductForm/utils';
import { SkuActionsMenu } from './SkuActionsMenu';
import { ProductHierarchy } from './ProductHierarchy';
import { skuBrand } from './skuFacets';
import {
  ActivePanelEdit,
  PalletPanelDraft,
  PanelEditTarget,
  ProductPanelDraft,
  SkuPanelDraft,
} from './panelEdit';
import {
  ProductGroupListing,
  isGroupTruncatedOnServer,
  productGroupKey,
  productGroupName,
  productGroupUnitTotals,
  productThumbnailSrc,
  representativeSkuOf,
} from './productGroups';

export interface ProductTableRowProps {
  group: ProductGroupListing;
  isExpanded: boolean;
  onToggleExpand: (groupKey: string) => void;
  isWarehouseUser: boolean;
  onArchiveSku: (sku: InventorySKUListing) => void;
  onArchiveProduct: (group: ProductGroupListing) => void;
  copiedKey: string | null;
  onCopy: (key: string, value: string) => void;
  onAddPackSize: (group: ProductGroupListing) => void;
  onAddAdditionalField: (groupKey: string, sku: InventorySKUListing) => void;
  onCloneProduct: (group: ProductGroupListing) => void;
  onCloneProductSkus: (group: ProductGroupListing) => void;
  onCloneHierarchy: (group: ProductGroupListing) => void;
  activeEdit: ActivePanelEdit | null;
  onDraftChange: (
    draft: ProductPanelDraft | SkuPanelDraft | PalletPanelDraft
  ) => void;
  onStartEdit: (target: PanelEditTarget) => void;
  onAddPallet: (groupKey: string, sku: InventorySKUListing) => void;
  onRemovePallet: (
    groupKey: string,
    sku: InventorySKUListing,
    palletIndex: number
  ) => void;
  expandedPanelKeys: Set<string>;
  onTogglePanel: (panelKey: string) => void;
}

export function ProductTableRow({
  group,
  isExpanded,
  onToggleExpand,
  isWarehouseUser,
  onArchiveSku,
  onArchiveProduct,
  copiedKey,
  onCopy,
  onAddPackSize,
  onAddAdditionalField,
  onCloneProduct,
  onCloneProductSkus,
  onCloneHierarchy,
  activeEdit,
  onDraftChange,
  onStartEdit,
  onAddPallet,
  onRemovePallet,
  expandedPanelKeys,
  onTogglePanel,
}: ProductTableRowProps) {
  const groupKey = productGroupKey(group);
  const name = productGroupName(group);
  const representative = representativeSkuOf(group);
  const brand = skuBrand(representative);
  const totals = productGroupUnitTotals(group);
  const productId = group.product?.productId ?? null;
  const isCopied = copiedKey === groupKey;
  const allVariantsArchived =
    group.skus.length > 0 && group.skus.every((sku) => sku.isArchived);
  const loadedFewerThanTotal = isGroupTruncatedOnServer(group);

  const thumbnailSrc = productThumbnailSrc(group);

  const subtext = brand ? `Brand: ${brand}` : '';

  return (
    <>
      <tr
        className={`cursor-pointer align-middle bg-gray-50/70 hover:bg-gray-100 ${
          isExpanded ? 'border-b-0' : ''
        }`}
        onClick={() => onToggleExpand(groupKey)}
      >
        <td className='w-10 px-2 py-4'>
          <button
            type='button'
            aria-expanded={isExpanded}
            aria-label={`${
              isExpanded ? 'Collapse' : 'Expand'
            } SKUs for ${name}`}
            className='rounded p-1 transition-colors hover:bg-gray-200'
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(groupKey);
            }}
          >
            <ChevronRightIcon
              className={`h-4 w-4 text-gray-500 transition-transform ${
                isExpanded ? 'rotate-90' : ''
              }`}
            />
          </button>
        </td>
        <td className='px-4 py-4'>
          <div className='flex items-center gap-3'>
            <EntityTile name={name} imageSrc={thumbnailSrc} />
            <div className='min-w-0'>
              <p className='truncate text-sm font-semibold text-gray-900'>
                {name}
              </p>
              {subtext && (
                <p className='truncate text-xs text-gray-500'>{subtext}</p>
              )}
            </div>
          </div>
        </td>
        <td className='px-4 py-4'>
          {productId ? (
            <div className='flex items-center gap-1.5 text-sm text-gray-600'>
              <span className='truncate'>{productId}</span>
              <button
                type='button'
                aria-label={`Copy product ID for ${name}`}
                className='shrink-0'
                onClick={(e) => {
                  e.stopPropagation();
                  onCopy(groupKey, productId);
                }}
              >
                {isCopied ? (
                  <CheckIcon className='h-4 w-4 text-emerald-600' />
                ) : (
                  <ClipboardDocumentIcon className='h-4 w-4 text-gray-400 hover:text-primary' />
                )}
              </button>
            </div>
          ) : (
            <span
              className='text-sm text-gray-400'
              title='These SKUs have no product ID yet — edit one and set its product to group them.'
            >
              Not yet grouped
            </span>
          )}
        </td>
        <td className='px-4 py-4 text-center text-sm text-gray-700'>
          {numberToLocale(group.skuCount)}
        </td>
        <td className='px-4 py-4 text-center text-sm text-gray-700'>
          {totals.totalProducts > 0 ? (
            numberToLocale(totals.totalProducts)
          ) : (
            <span className='text-gray-400'>—</span>
          )}
          {totals.totalProducts > 0 && (
            <span className='ml-1 text-xs text-gray-500'>products</span>
          )}
        </td>
        <td className='px-4 py-4 text-center'>
          {allVariantsArchived ? (
            <Badge intent='neutral' label='Archived' />
          ) : (
            <Badge intent='info' label='Active' />
          )}
        </td>
        <td className='px-4 py-4' onClick={(e) => e.stopPropagation()}>
          <div className='flex items-center justify-end'>
            {isWarehouseUser && (
              <SkuActionsMenu
                id={productId ?? name}
                options={[
                  {
                    label: 'Edit',
                    action: () => {
                      if (!isExpanded) onToggleExpand(groupKey);
                      onStartEdit({ kind: 'product', groupKey });
                    },
                  },
                  { label: 'Clone', action: () => onCloneProduct(group) },
                  { label: 'Archive', action: () => onArchiveProduct(group) },
                ]}
              />
            )}
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={7} className='bg-white p-0'>
            <ProductHierarchy
              group={group}
              activeEdit={activeEdit}
              onDraftChange={onDraftChange}
              onStartEdit={onStartEdit}
              onAddPallet={onAddPallet}
              onRemovePallet={onRemovePallet}
              onAddSku={onAddPackSize}
              onAddAdditionalField={onAddAdditionalField}
              onCloneProductSkus={onCloneProductSkus}
              onCloneHierarchy={onCloneHierarchy}
              onArchiveSku={onArchiveSku}
              expandedPanelKeys={expandedPanelKeys}
              onTogglePanel={onTogglePanel}
              canManageSkus={isWarehouseUser}
              productImageSrc={thumbnailSrc}
              productName={name}
            />
            {loadedFewerThanTotal && (
              <p className='px-4 pb-3 pl-20 text-xs text-gray-400'>
                Showing {group.skus.length} of {group.skuCount} SKUs.
              </p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
